import React from 'react';
import styled from '@emotion/styled';
import Image from 'next/image';
import SolecarLogo from './icon/SolecarLogo';
import { fontFrame } from '../../styles/mixin/font';
import { BLACK, DARK_BLUE, LIGHT_BLUE, WHITE } from '../../styles/constants/color';
import Span from './text/Span';
import Link from 'next/link';
import { useRouter, NextRouter } from 'next/router';

interface Props {
  error?: Error;
}

const Base = styled.div`
  display: grid;
  align-items: start;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  padding-top: 120px;
`;

const Article = styled.article`
  display: grid;
  grid-template-rows: 180px 1fr;
  row-gap: 30px;
  width: 768px;
  height: auto;
  padding: 8px;

  @media (max-width: 768px) {
    width: 100vw;
  }
`;

const Header = styled.section`
  display: grid;
  grid-template-columns: 180px 1fr;
`;

const TitleWrapper = styled.div`
  display: grid;
  grid-template-rows: 1fr 1fr;
  row-gap: 8px;
  justify-self: start;
  align-self: end;
`;

const Body = styled.section`
  width: 100%;
  height: auto;
`;

const Title = styled.h1`
  ${fontFrame('500', '50px', '1', '#30c3c1')};
`;

const Description = styled.p`
  ${fontFrame('500', '30px', '1', BLACK)};
  margin-top: 10px;
  margin-bottom: 20px;

  &:last-of-type {
    line-height: 50px;
    margin: 0;
  }
`;

const TextBox = styled.strong<{ borderColor: string; fontColor: string }>`
  color: ${props => props.fontColor};
  border: 2px solid ${props => props.borderColor};
  padding: 0 8px;
  border-radius: 5px;
  cursor: pointer;
`;

const Details = styled.details`
  width: 100%;
  height: auto;
  margin-top: 30px;
  max-height: 350px;
`;

const Summary = styled.summary`
  ${fontFrame('500', '35px', '1', WHITE)};
  padding: 16px;
  background-color: #ff5555;
  border-radius: 8px;
  cursor: pointer;
`;

const MessageBox = styled.div`
  width: 100%;
  height: auto;
  max-height: 350px;
  overflow-y: overlay;
  margin-top: 10px;
  background-color: #292c30;
  padding: 8px;
  border-radius: 8px;
`;

const Message = styled.strong`
  ${fontFrame('500', '20px', '1', WHITE)};
  border-bottom: 1px solid ${WHITE};
  margin-bottom: 8px;
  word-break: break-all;
  cursor: pointer;
`;

const StackMessage = styled.pre`
  ${fontFrame('500', '15px', '1', WHITE)};
  word-break: break-all;
  white-space: break-spaces;
  margin-top: 15px;
`;

/**
 *  ErrorBoundary fallback 컴포넌트
 * @param {Error} error error 객체
 *  */
function ErrorComponent({ error }: Props): JSX.Element {
  const router: NextRouter = useRouter();
  const { pathname } = router;
  /**
   *  HOME 혹은 REFRESH strong 클릭시 페이지 이동해주는 클릭 이벤트
   * @param {React.MouseEvent} e 클릭 이벤트 객체
   *  */
  const onClick = (e: React.MouseEvent): void => {
    const innerHTML: string = e.currentTarget.innerHTML;
    router.replace(innerHTML === 'HOME' ? '/' : pathname);

    setTimeout(() => {
      window.location.reload();
    }, 500);
  };
  return (
    <Base>
      <Article>
        <Header>
          <Image
            src='/assets/images/settings.gif'
            width={180}
            height={180}
            priority
            alt='톱니바퀴 움직이는 이미지'
          />
          <TitleWrapper>
            <SolecarLogo />
            <Span fontSize='20px'>자동차 판매지원 시스템</Span>
          </TitleWrapper>
        </Header>
        <Body>
          <Title>ERROR !!</Title>
          <Description>에러가 발생했습니다.</Description>
          <Description>
            <TextBox borderColor={DARK_BLUE} fontColor={DARK_BLUE} onClick={onClick}>
              HOME
            </TextBox>
            &nbsp;혹은&nbsp;
            <TextBox borderColor={LIGHT_BLUE} fontColor={LIGHT_BLUE} onClick={onClick}>
              REFRESH
            </TextBox>
            &nbsp;를 눌러주세요.
          </Description>
          <Details>
            <Summary>에러내용 확인</Summary>
            <MessageBox>
              <Link
                href={`https://www.google.com/search?q=${error?.message}`}
                passHref
                target='_blank'
              >
                <Message>Message: {error?.message}</Message>
              </Link>
              <StackMessage>{error?.stack}</StackMessage>
            </MessageBox>
          </Details>
        </Body>
      </Article>
    </Base>
  );
}

export default ErrorComponent;
